import { useCallback, useEffect, useState } from 'react';

/**
 * The theme preference (2026-08-29).
 *
 * Three states, not two. `'system'` is the default and is **not** the same as whichever of
 * light/dark the system currently is: a reader on `'system'` follows their OS when it changes at
 * sunset, a reader who picked `'light'` does not. Collapsing this to a boolean loses that, and
 * loses the ability to go back to following the OS once you have touched the control.
 *
 * The mechanism is entirely CSS — `theme/global.css` holds both palettes, the media query serves
 * the system case, and `data-theme` on the document element overrides it. This hook writes that
 * one attribute and nothing else, which is why no component needs to know the theme to be themed.
 * The chart is the single exception, and even there it asks CSS (`--c-series-*`), not this hook.
 */
export type ThemeChoice = 'system' | 'light' | 'dark';

/**
 * Deliberately NOT namespaced by the signed-in address, unlike `store/dataStore`.
 *
 * That cache holds one user's figures and must not survive a sign-out on a shared machine. This
 * holds "my eyes prefer dark", which is a property of the person at the keyboard and should
 * survive both sign-out and the next sign-in — including the sign-in screen itself, which is the
 * first thing a reader sees and the worst place to flash white.
 */
const KEY = 'bob.theme';

const CHOICES: readonly ThemeChoice[] = ['system', 'light', 'dark'];

function read(): ThemeChoice {
  try {
    const stored = localStorage.getItem(KEY);
    return CHOICES.includes(stored as ThemeChoice) ? (stored as ThemeChoice) : 'system';
  } catch {
    // Private mode, or storage disabled. A theme preference is not worth an error boundary.
    return 'system';
  }
}

/**
 * Writes the RESOLVED theme, never `'system'`.
 *
 * `global.css` has one dark block keyed on `data-theme` and no `prefers-color-scheme` copy of it,
 * so `'system'` is not a value the stylesheet can act on — this has to answer the media query and
 * write light or dark. Removing the attribute for `'system'` (which is what an earlier version
 * did) would leave the page light on a machine set to dark.
 *
 * Applied to `<html>` rather than `<body>`: `color-scheme` on the root is what makes the browser
 * paint form controls, scrollbars and the overscroll area in the matching shade. On body, the page
 * is dark while the gutter beyond it stays white.
 */
function apply(resolved: 'light' | 'dark') {
  document.documentElement.setAttribute('data-theme', resolved);
}

export function useTheme() {
  const [choice, setChoice] = useState<ThemeChoice>(read);

  /**
   * What the reader is actually looking at, which is what the toggle's icon has to show. On
   * `'system'` that is a question only the OS can answer, and the answer can change while the tab
   * is open — hence the listener rather than a one-time read.
   */
  const [systemDark, setSystemDark] = useState(
    () =>
      typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches,
  );
  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const query = matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent) => setSystemDark(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const resolved: 'light' | 'dark' =
    choice === 'system' ? (systemDark ? 'dark' : 'light') : choice;

  /**
   * Two things, one effect, because they must not disagree: the attribute the stylesheet reads and
   * the choice the next page load reads. Keyed on `resolved` as well as `choice` so that a reader
   * on `'system'` follows their machine when it flips at sunset — that is the whole point of the
   * third state.
   */
  useEffect(() => {
    apply(resolved);
    try {
      localStorage.setItem(KEY, choice);
    } catch {
      // Non-fatal: the theme still applies for this session, it just will not be remembered.
    }
  }, [choice, resolved]);

  /** Cycles system → light → dark → system, so the control can return to following the OS. */
  const cycle = useCallback(() => {
    setChoice((current) => CHOICES[(CHOICES.indexOf(current) + 1) % CHOICES.length]!);
  }, []);

  return { choice, resolved, cycle, setChoice };
}
